import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "./LoadingIndicator";

interface LanguageProviderProps {
  children: React.ReactNode;
}

/**
 * LanguageProvider component that sets the application language based on the URL path
 * This ensures that when a user navigates directly to a language-specific URL (e.g., /en or /ar),
 * the application will use that language.
 */
const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLanguageSet, setIsLanguageSet] = useState(false);

  useEffect(() => {
    // Extract language code from the URL path
    const pathSegments = location.pathname.split("/");
    const langCode = pathSegments[1]; // The first segment after the leading slash

    // Check if we're on a language-specific path
    if (langCode && (langCode === "en" || langCode === "ar")) {
      // Set the language if it's different from the current one
      if (i18n.language !== langCode) {
        i18n.changeLanguage(langCode);
      }
      setIsLanguageSet(true);
    } else {
      // If we're not on a language path but somehow got here, redirect to the appropriate language
      const userLang = localStorage.getItem("i18nextLng") || "en";
      navigate(`/${userLang}${location.search}`, { replace: true });
    }
  }, [location.pathname, i18n, navigate, location.search]);

  // Show a loading indicator while setting language
  if (!isLanguageSet) {
    return <LoadingIndicator message="Setting language..." />;
  }

  return <>{children}</>;
};

export default LanguageProvider;
